import { useEffect, useRef } from 'react';
import { useAsync, useExit } from '@wix/cli-ui-kit';
import type { AsyncStatus } from '@wix/cli-ui-kit';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { ProjectModel } from '../../../model.js';
import { startDevServer } from '../utils/startDevServer.js';
import type { DevServerController } from '../utils/startDevServer.js';

interface DevServerLogger {
  log: (line: string) => void;
  error: (line: string) => void;
}

interface UseDevServerLocalUrlOptions {
  model: ProjectModel;
  port?: number;
  allowedHosts?: string;
  logger: DevServerLogger;
}

export function useDevServerLocalUrl({
  model,
  port,
  allowedHosts,
  logger,
}: UseDevServerLocalUrlOptions): AsyncStatus<string> {
  const exit = useExit();

  const devServer = useRef<DevServerController | null>(null);
  const isUnmounted = useRef(false);

  const { status } = useAsync(async () => {
    const server = await startDevServer({
      model,
      port,
      allowedHosts,
      logger,
      onUnexpectedExit: () =>
        exit(
          new CliError({
            cause: undefined,
            code: CliErrorCode.DevCommandFailed(),
          })
        ),
    });

    if (isUnmounted.current) {
      void server.stop();
    } else {
      devServer.current = server;
    }

    return server.ready;
  }, []);

  useEffect(() => {
    return () => {
      isUnmounted.current = true;
      const server = devServer.current;

      if (server) {
        devServer.current = null;
        void server.stop();
      }
    };
  }, []);

  return status;
}
